// EventContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  getAllEvents, 
  getEventById, 
  createEvent as createEventAPI,
  updateEvent as updateEventAPI,
  deleteEvent as deleteEventAPI,
  participateInEvent,
  unregisterFromEvent,
  isUserParticipating
} from '../api/events';
import { AuthContext } from './AuthContext';

export const EventContext = createContext();

export const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, userData } = useContext(AuthContext);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedEvents = await getAllEvents();
      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getEvent = async (eventId) => {
    try {
      const event = await getEventById(eventId);
      return event;
    } catch (err) {
      console.error('Error fetching event:', err);
      throw err;
    }
  };

  const createEvent = async (eventData) => {
    try {
      const eventId = await createEventAPI(eventData);
      await fetchEvents(); // Refresh events list
      return eventId;
    } catch (err) {
      console.error('Error creating event:', err);
      throw err;
    }
  };

  const updateEvent = async (eventId, eventData) => {
    try {
      await updateEventAPI(eventId, eventData);
      await fetchEvents(); // Refresh events list
    } catch (err) {
      console.error('Error updating event:', err);
      throw err;
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await deleteEventAPI(eventId);
      await fetchEvents(); // Refresh events list
    } catch (err) {
      console.error('Error deleting event:', err);
      throw err;
    }
  };

  const registerForEvent = async (eventId) => {
    try {
      if (!user || !userData) {
        throw new Error('Please log in to participate in events');
      }
      
      // Get user's display name from available fields
      const userName = userData.name || 
                      (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                      userData.firstName || 
                      userData.lastName ||
                      userData.email || 
                      user.email;
      await participateInEvent(eventId, user.uid, userName);
      await fetchEvents(); // Refresh events list
      return true;
    } catch (err) {
      console.error('Error registering for event:', err);
      throw err;
    }
  };

  const unregisterFromEventHandler = async (eventId) => {
    try {
      if (!user) {
        throw new Error('Please log in');
      }
      
      await unregisterFromEvent(eventId, user.uid);
      await fetchEvents(); // Refresh events list
      return true;
    } catch (err) {
      console.error('Error unregistering from event:', err);
      throw err;
    }
  };

  const checkUserParticipation = async (eventId) => {
    try {
      if (!user) {
        return false;
      }
      
      return await isUserParticipating(eventId, user.uid);
    } catch (err) {
      console.error('Error checking participation:', err);
      return false;
    }
  };

  const value = {
    events,
    loading,
    error,
    fetchEvents,
    getEvent,
    createEvent,
    updateEvent,
    deleteEvent,
    registerForEvent,
    unregisterFromEventHandler,
    checkUserParticipation,
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};
